import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ResourceClient } from '../../../api/resource';
import { logError } from '../../../utils/logger';
import { SectionTitle } from '../../shared/components';
import { Table } from '../../shared/components/data-display';
import { ConfirmationModal } from '../../shared/components/feedback';
import useAsyncData from '../../shared/hooks/useAsyncData';
import { uploadFile } from '../../shared/service';

export type FieldType =
  | 'text'
  | 'password'
  | 'number'
  | 'textarea'
  | 'boolean'
  | 'select'
  | 'autocomplete'
  | 'date'
  | 'time'
  /** An uploaded image; the value is its file id (served at /api/file/{id}). */
  | 'image';

export interface FieldOption {
  label: string;
  value: string | number;
}

export interface ResourceField {
  /** Key sent to the API — snake_case, matching the controller's request body. */
  name: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  /** Required in the Add dialog only, e.g. a password that editing may leave blank. */
  requiredOnCreate?: boolean;
  /** Options for `type: 'select'`, or the static fallback for `type: 'autocomplete'`. */
  options?: FieldOption[];
  /**
   * `type: 'autocomplete'` only - fetches the searchable list the first time the dialog
   * opens. The result is cached for the life of the page, so reopening costs nothing.
   */
  loadOptions?: () => Promise<FieldOption[]>;
  /**
   * `type: 'autocomplete'` only. Defaults to true: the field keeps whatever the admin
   * types even when it matches no option, which is what free-text columns holding legacy
   * values need. Set false to accept listed values only.
   */
  allowCustomValue?: boolean;
  helperText?: string;
  /** Hide from the create form (e.g. immutable `job_id` on update-only forms). */
  hideOnEdit?: boolean;
  defaultValue?: unknown;
}

export interface ResourceColumn {
  id: string;
  /** Key read off the row — camelCase, matching the JPA entity serialization. */
  dataKey: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  renderCell?: (value: any, row: any) => React.ReactNode;
}

export interface ResourceManagerProps<T> {
  title: string;
  /** Singular noun used in buttons and confirmation copy, e.g. "Department". */
  entityName: string;
  client: ResourceClient<T>;
  columns: ResourceColumn[];
  fields: ResourceField[];
  /** Set false for read-only resources. */
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  /** Maps an API row onto form values when opening the edit dialog. */
  toFormValues?: (row: any) => Record<string, unknown>;
  /** Extra query params for the list call. */
  listParams?: Record<string, unknown>;
}

const buildDefaults = (fields: ResourceField[]): Record<string, unknown> =>
  fields.reduce<Record<string, unknown>>((acc, field) => {
    acc[field.name] = field.defaultValue ?? (field.type === 'boolean' ? true : '');
    return acc;
  }, {});

/**
 * List + create/edit/delete for the uniform CRUD resources the backend exposes.
 * One component covers Departments and all ten V5 lookup tables, which share an
 * identical route shape and response envelope.
 */
export default function ResourceManager<T extends { id?: number | string }>({
  title,
  entityName,
  client,
  columns,
  fields,
  canCreate = true,
  canEdit = true,
  canDelete = true,
  toFormValues,
  listParams,
}: ResourceManagerProps<T>) {
  const fetcher = useCallback(() => client.list(listParams), [client, listParams]);

  const { data, loading, error, refetch } = useAsyncData<T[]>(fetcher, [], {
    code: `${entityName.toUpperCase().replace(/\s+/g, '_')}_LIST_FAILED`,
    errorMessage: `Failed to load ${title.toLowerCase()}`,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<any>(null);
  const [values, setValues] = useState<Record<string, unknown>>(() => buildDefaults(fields));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);

  /* ── Searchable option lists ───────────────────────────────────────────── */

  const asyncFields = useMemo(() => fields.filter((field) => field.loadOptions), [fields]);
  const [asyncOptions, setAsyncOptions] = useState<Record<string, FieldOption[]>>({});
  const [optionsLoading, setOptionsLoading] = useState(false);

  /**
   * Fetched lazily on the first dialog open rather than on mount: the list is only ever
   * needed by the form, and most visits to the page never open it. Whatever has already
   * been fetched is reused, so typing filters an in-memory list with no request per
   * keystroke — these lists are small enough that a server round trip would be slower.
   */
  useEffect(() => {
    if (!dialogOpen) return;

    const pending = asyncFields.filter((field) => !asyncOptions[field.name]);
    if (pending.length === 0) return;

    let active = true;
    setOptionsLoading(true);

    Promise.all(
      pending.map(async (field) => [field.name, await field.loadOptions!()] as const)
    )
      .then((entries) => {
        if (active) setAsyncOptions((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      })
      .catch((err) => {
        if (!active) return;
        toast.error(
          logError({
            message: `Failed to load options for ${entityName.toLowerCase()}`,
            error: err,
            code: 'RESOURCE_OPTIONS_FETCH_FAILED',
          })
        );
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [dialogOpen, asyncFields, asyncOptions, entityName]);

  const openCreate = () => {
    setEditingRow(null);
    setValues(buildDefaults(fields));
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: any) => {
    setEditingRow(row);
    setValues(
      toFormValues
        ? toFormValues(row)
        : fields.reduce<Record<string, unknown>>((acc, field) => {
            // API returns camelCase; form keys are the snake_case request keys.
            const camel = field.name.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
            acc[field.name] = row[camel] ?? row[field.name] ?? '';
            return acc;
          }, {})
    );
    setFormError(null);
    setDialogOpen(true);
  };

  const setField = (name: string, value: unknown) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    const missing = fields
      .filter((f) => (f.required || (f.requiredOnCreate && !editingRow)) && !(editingRow && f.hideOnEdit))
      .filter((f) => values[f.name] === '' || values[f.name] === null || values[f.name] === undefined);

    if (missing.length > 0) {
      setFormError(`Required: ${missing.map((f) => f.label).join(', ')}`);
      return;
    }

    // Only send keys the user actually filled — every PUT is a partial update.
    const payload = fields.reduce<Record<string, unknown>>((acc, field) => {
      if (editingRow && field.hideOnEdit) return acc;
      const value = values[field.name];
      if (value === '' || value === undefined) return acc;
      acc[field.name] = field.type === 'number' ? Number(value) : value;
      return acc;
    }, {});

    setSaving(true);
    setFormError(null);
    try {
      if (editingRow) {
        await client.update(editingRow.id, payload);
        toast.success(`${entityName} updated`);
      } else {
        await client.create(payload);
        toast.success(`${entityName} created`);
      }
      setDialogOpen(false);
      await refetch();
    } catch (err) {
      setFormError(
        logError({
          message: `Failed to save ${entityName.toLowerCase()}`,
          error: err,
          code: 'RESOURCE_SAVE_FAILED',
        })
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await client.remove(pendingDelete.id);
      toast.success(`${entityName} deleted`);
      setPendingDelete(null);
      await refetch();
    } catch (err) {
      toast.error(
        logError({
          message: `Failed to delete ${entityName.toLowerCase()}`,
          error: err,
          code: 'RESOURCE_DELETE_FAILED',
        })
      );
    } finally {
      setDeleting(false);
    }
  };

  const tableColumns = useMemo(() => {
    if (!canEdit && !canDelete) return columns;

    return [
      ...columns,
      {
        id: 'actions',
        dataKey: 'id',
        label: 'Actions',
        align: 'center' as const,
        renderCell: (_value: unknown, row: any) => (
          <Stack direction='row' gap={1} justifyContent='center'>
            {canEdit && (
              <Tooltip title={`Edit ${entityName}`}>
                <IconButton size='small' onClick={() => openEdit(row)} aria-label='Edit'>
                  <Icon icon='mdi:pencil-outline' width={18} />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={`Delete ${entityName}`}>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => setPendingDelete(row)}
                  aria-label='Delete'
                >
                  <Icon icon='mdi:trash-can-outline' width={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, canEdit, canDelete, entityName]);

  return (
    <Stack gap={3}>
      <SectionTitle
        title={title}
        action={
          canCreate ? (
            <Button
              variant='contained'
              startIcon={<Icon icon='mdi:plus' />}
              onClick={openCreate}
              disabled={loading}
            >
              Add {entityName}
            </Button>
          ) : null
        }
      />

      {error && (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Table
        columns={tableColumns}
        data={data}
        loading={loading}
        emptyMessage={`No ${title.toLowerCase()} found`}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>
          {editingRow ? `Edit ${entityName}` : `Add ${entityName}`}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2.5} sx={{ paddingTop: 1 }}>
            {formError && <Alert severity='error'>{formError}</Alert>}

            {fields
              .filter((field) => !(editingRow && field.hideOnEdit))
              .map((field) => {
                if (field.type === 'boolean') {
                  return (
                    <FormControlLabel
                      key={field.name}
                      control={
                        <Switch
                          checked={Boolean(values[field.name])}
                          onChange={(e) => setField(field.name, e.target.checked)}
                        />
                      }
                      label={field.label}
                    />
                  );
                }

                if (field.type === 'image') {
                  return (
                    <ImageField
                      key={field.name}
                      field={field}
                      value={values[field.name]}
                      onChange={(next) => setField(field.name, next)}
                    />
                  );
                }

                if (field.type === 'autocomplete') {
                  return (
                    <AutocompleteField
                      key={field.name}
                      field={field}
                      value={values[field.name]}
                      options={asyncOptions[field.name] ?? field.options ?? []}
                      loading={optionsLoading && !asyncOptions[field.name]}
                      onChange={(next) => setField(field.name, next)}
                    />
                  );
                }

                const isSelect = field.type === 'select';

                return (
                  <TextField
                    key={field.name}
                    select={isSelect}
                    label={field.label}
                    required={field.required || (field.requiredOnCreate && !editingRow)}
                    helperText={field.helperText}
                    type={
                      field.type === 'number'
                        ? 'number'
                        : field.type === 'date'
                          ? 'date'
                          : field.type === 'time'
                            ? 'time'
                            : field.type === 'password'
                              ? 'password'
                              : 'text'
                    }
                    autoComplete={field.type === 'password' ? 'new-password' : undefined}
                    multiline={field.type === 'textarea'}
                    minRows={field.type === 'textarea' ? 3 : undefined}
                    value={values[field.name] ?? ''}
                    onChange={(e) => setField(field.name, e.target.value)}
                    slotProps={
                      field.type === 'date' || field.type === 'time'
                        ? { inputLabel: { shrink: true } }
                        : undefined
                    }
                    fullWidth
                  >
                    {isSelect &&
                      (field.options ?? []).map((option) => (
                        <MenuItem key={String(option.value)} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                  </TextField>
                );
              })}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={Boolean(pendingDelete)}
        title={`Delete ${entityName}`}
        message={`This will permanently delete this ${entityName.toLowerCase()}. Continue?`}
        confirmText={deleting ? 'Deleting…' : 'Delete'}
        icon='mdi:alert-outline'
        loading={deleting}
        onConfirm={handleDelete}
        onClose={() => setPendingDelete(null)}
      />
    </Stack>
  );
}

/** Compares an option against the held value, either of which can be bare text. */
const optionKey = (option: string | FieldOption): string =>
  String(typeof option === 'string' ? option : option.value);

interface AutocompleteFieldProps {
  field: ResourceField;
  value: unknown;
  options: FieldOption[];
  loading: boolean;
  onChange: (value: string | number) => void;
}

/**
 * Type-to-filter picker over an option list. The stored value is the option's `value`,
 * so a field backed by a free-text column (e.g. `users.department`, which holds the
 * department name rather than an id) supplies options whose value is that name.
 *
 * Free text is kept by default: the list is a set of suggestions, not a constraint, and
 * silently dropping a value the list does not know would erase existing rows on save.
 */
/** Upload an image and keep its file id; shows what is stored now. */
function ImageField({
  field,
  value,
  onChange,
}: {
  field: ResourceField;
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const id = value == null || value === '' ? null : value;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const uploaded = await uploadFile(file);
      if (uploaded.id == null) throw new Error('The upload returned no file id');
      onChange(uploaded.id);
    } catch (err) {
      toast.error(logError({ message: `Could not upload the ${field.label.toLowerCase()}`, error: err, code: 'IMAGE_UPLOAD_FAILED' }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack gap={0.5}>
      <Typography variant='body2' color='text.secondary'>{field.label}</Typography>
      <Stack direction='row' gap={2} alignItems='center'>
        {id ? (
          <Box component='img' src={`${import.meta.env.VITE_API_URL}/api/file/${id}`} alt={field.label}
            sx={{ maxHeight: 56, maxWidth: 200, border: 1, borderColor: 'divider', borderRadius: 1, p: 0.5, bgcolor: 'background.paper' }} />
        ) : (
          <Typography variant='body2' color='text.disabled'>None uploaded</Typography>
        )}
        <Button component='label' size='small' variant='outlined' disabled={uploading}>
          {uploading ? 'Uploading…' : id ? 'Replace' : 'Upload'}
          <input hidden type='file' accept='image/png,image/jpeg' onChange={(e) => pick(e.target.files?.[0])} />
        </Button>
      </Stack>
      {field.helperText && <Typography variant='caption' color='text.secondary'>{field.helperText}</Typography>}
    </Stack>
  );
}

function AutocompleteField({ field, value, options, loading, onChange }: AutocompleteFieldProps) {
  const allowCustomValue = field.allowCustomValue !== false;
  const current = value == null || value === '' ? '' : String(value);
  const selected = options.find((option) => String(option.value) === current);

  return (
    <Autocomplete
      options={options}
      freeSolo={allowCustomValue}
      autoHighlight
      handleHomeEndKeys
      selectOnFocus
      clearOnBlur={!allowCustomValue}
      loading={loading}
      // Text typed but never picked off the list stays as a bare string.
      value={selected ?? (current || null)}
      // `freeSolo` is dynamic here, so every option and value widens to `string | FieldOption`.
      getOptionLabel={(option: string | FieldOption) =>
        typeof option === 'string' ? option : option.label
      }
      isOptionEqualToValue={(option: string | FieldOption, picked: string | FieldOption) =>
        optionKey(option) === optionKey(picked)
      }
      onChange={(_event, next) => {
        if (next == null) return onChange('');
        onChange(typeof next === 'string' ? next : next.value);
      }}
      onInputChange={(_event, next, reason) => {
        // 'reset' fires when the list re-renders the selected label; echoing it back
        // would overwrite a custom value with the label of whatever was highlighted.
        if (reason === 'input' && allowCustomValue) onChange(next);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          required={field.required}
          helperText={field.helperText}
          fullWidth
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color='inherit' size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
